import { useQuery } from "@/api/utils/useQuery"
import { IAccount } from "@/models/account/account.types"
import {
  AccountParserV2,
  PropertyAccountParser,
} from "@/models/account/account.parser"
import { QueryResult } from "@/api/utils/types"
import { HasuraQueries } from "@/api/hasura/queries"
import { BackofficeRestQueries } from "@/api/backofficeRest/queries"
import {
  AccountDetailsResponse,
  AccountLoyaltyDrawResponse,
  PropertyAccount,
  SuggestedPaymentResponse,
  UnbilledReadingCostsResponse,
  WarrantStatus,
} from "@/api/backofficeRest/generated/model"
import { getEmptyAccount } from "@/models/account/account"
import TrackingService from "@/services/TrackingService"

export interface IAccountService {
  getAccount(accountNumber: string): QueryResult<IAccount>
  getAccountsByProperty(propertyId: number | null): QueryResult<IAccount[]>
  getCoreIdByAccountNumber(accountNumber: string): QueryResult<number | null>
  getCustomerAccounts(customerId: number): QueryResult<string[]>
  hasOpenComplaint(coreId: number): QueryResult<boolean | null>
  getWarrantStatus(coreId: number | null): QueryResult<boolean | null>
  getActiveDrawInfoForAccount(
    billingAccountId: number,
  ): QueryResult<AccountLoyaltyDrawResponse | null>
  searchAccountsByPhoneOrEmail(searchTerm: string): QueryResult<string[]>
  searchAccountsBySurname(surname: string): QueryResult<string[]>
  searchAccountsByPostcode(postcode: string): QueryResult<string[]>
  searchMeterPointsByIdentifier(identifier: string): QueryResult<number[]>
  searchAccountsByMeterPointIds(meterPointIds: number[]): QueryResult<number[]>
  getAccountNumbersByCoreIds(coreIds: number[]): QueryResult<string[]>
  getAccountNumberMap(coreIds: number[]): QueryResult<Record<number, string>>
}

class AccountServiceClass implements IAccountService {
  getAccount(accountNumber: string) {
    return useQuery({
      ...BackofficeRestQueries.account.detailsByAccountNumber(accountNumber),
      parserFn: (rawData: AccountDetailsResponse) => {
        if (!rawData) return getEmptyAccount()
        TrackingService.sendEvent("Customer:loadDetails:success")
        return AccountParserV2.toDomain(rawData)
      },
      useRawResponse: true,
      placeholderData: getEmptyAccount(),
      showSnackbarError: true,
      meta: {
        errorMessage: `Failed to fetch account: ${accountNumber}`,
      },
    })
  }

  getAccountsByProperty(propertyId: number | null): QueryResult<IAccount[]> {
    return useQuery<PropertyAccount[], IAccount[]>({
      ...BackofficeRestQueries.propertyAccounts.atProperty(propertyId ?? -1),
      placeholderData: [],
      useRawResponse: true,
      parserFn: (data) => (data ?? []).map(PropertyAccountParser.toDomain),
      enabled: !!propertyId,
    })
  }

  getCoreIdByAccountNumber(accountNumber: string): QueryResult<number | null> {
    return useQuery({
      ...HasuraQueries.account.getCoreIdByAccountNumber(accountNumber),
      useRawResponse: true,
      placeholderData: null,
      parserFn: (rawData) => rawData.billing_account[0]?.id ?? null,
    })
  }

  getCustomerIdByAccountNumber(
    accountNumber: string,
  ): QueryResult<number | null> {
    return useQuery({
      ...HasuraQueries.account.getCustomerIdByAccountNumber(accountNumber),
      useRawResponse: true,
      placeholderData: null,
      parserFn: (rawData) => rawData.billing_account[0]?.customer_id ?? null,
    })
  }

  getCustomerAccounts(customerId: number): QueryResult<string[]> {
    return useQuery({
      ...HasuraQueries.account.getAssociatedAccounts(customerId),
      placeholderData: [],
      useRawResponse: true,
      parserFn: (rawData) => {
        if (!rawData) return []
        return rawData.billing_account.map((account) => account.number)
      },
    })
  }

  getUnbilledReadingCosts(billingAccountId: number | null) {
    return useQuery({
      ...BackofficeRestQueries.account.unbilledReadingCosts(
        billingAccountId ?? -1,
      ),
      enabled: !!billingAccountId,
      useRawResponse: true,
      parserFn: (rawData: UnbilledReadingCostsResponse) =>
        rawData?.totalCostInPounds ?? 0,
    })
  }

  hasOpenComplaint(coreId: number | null) {
    return useQuery<boolean, boolean | null>({
      ...BackofficeRestQueries.complaint.hasOpen(coreId ?? -1),
      placeholderData: null,
      useRawResponse: true,
      parserFn: (data) => data ?? null,
      enabled: !!coreId,
    })
  }

  getWarrantStatus(coreId: number | null) {
    return useQuery<WarrantStatus, boolean | null>({
      ...BackofficeRestQueries.warrants.status(coreId ?? -1),
      placeholderData: null,
      useRawResponse: true,
      parserFn: (data) => data?.isActive ?? null,
      enabled: !!coreId,
    })
  }

  getSuggestedPayments(coreId: number) {
    return useQuery({
      ...BackofficeRestQueries.account.suggestedPayments(coreId),
      placeholderData: null,
      useRawResponse: true,
      parserFn: (data: SuggestedPaymentResponse) => data ?? null,
    })
  }

  getActiveDrawInfoForAccount(billingAccountId: number) {
    return useQuery({
      ...BackofficeRestQueries.account.getActiveDrawInfoForAccount(
        billingAccountId,
      ),
      placeholderData: null,
      useRawResponse: true,
      parserFn: (data: AccountLoyaltyDrawResponse) =>
        data ?? { eligibleForDraw: false, alreadyEntered: false },
    })
  }

  searchAccountsByPhoneOrEmail(searchTerm: string) {
    return useQuery({
      ...HasuraQueries.account.search._ctx.byPhoneOrEmail(searchTerm),
      useRawResponse: true,
      placeholderData: [],
      parserFn: (rawData) => {
        const accountNumberSet = new Set<string>()
        for (const contact of rawData.contact ?? []) {
          for (const bac of contact.billing_account_contacts ?? []) {
            if (bac.billing_account?.number) {
              accountNumberSet.add(bac.billing_account.number)
            }
          }
        }
        return Array.from(accountNumberSet)
      },
    })
  }

  searchAccountsBySurname(surname: string) {
    return useQuery({
      ...HasuraQueries.account.search._ctx.bySurname(surname),
      useRawResponse: true,
      placeholderData: [],
      parserFn: (rawData) => {
        const accountNumberSet = new Set<string>()
        for (const customer of rawData.customer ?? []) {
          for (const ba of customer.billing_accounts ?? []) {
            if (ba.number) {
              accountNumberSet.add(ba.number)
            }
          }
        }
        return Array.from(accountNumberSet)
      },
    })
  }

  searchAccountsByPostcode(postcode: string) {
    return useQuery({
      ...HasuraQueries.account.search._ctx.byPostcode(postcode),
      useRawResponse: true,
      placeholderData: [],
      parserFn: (rawData) => {
        const accountNumberSet = new Set<string>()
        for (const contact of rawData.contact ?? []) {
          for (const bac of contact.billing_account_contacts ?? []) {
            if (bac.billing_account?.number) {
              accountNumberSet.add(bac.billing_account.number)
            }
          }
        }
        return Array.from(accountNumberSet)
      },
    })
  }

  searchMeterPointsByIdentifier(identifier: string) {
    return useQuery({
      ...HasuraQueries.account.search._ctx.byMeterPointIdentifier(identifier),
      useRawResponse: true,
      placeholderData: [],
      parserFn: (rawData) => {
        return rawData.meter_point?.map((mp) => mp.id) || []
      },
    })
  }

  searchAccountsByMeterPointIds(meterPointIds: number[]) {
    return useQuery({
      ...HasuraQueries.account.search._ctx.byMeterPointIds(meterPointIds),
      useRawResponse: true,
      placeholderData: [],
      parserFn: (rawData) => {
        return (
          rawData.billing_account_ids_with_meter_point_ids?.map(
            (item) => item.id,
          ) || []
        )
      },
    })
  }

  getAccountNumbersByCoreIds(coreIds: number[]) {
    return useQuery({
      ...HasuraQueries.account.search._ctx.getAccountNumbersByCoreIds(coreIds),
      useRawResponse: true,
      placeholderData: [],
      parserFn: (rawData) => {
        return rawData.billing_account?.map((ba) => ba.number) || []
      },
    })
  }

  // Distinct key from getAccountNumbersByCoreIds: useQuery caches by
  // String(queryKey), so a shared key would collide two different parsed shapes.
  getAccountNumberMap(coreIds: number[]): QueryResult<Record<number, string>> {
    return useQuery({
      ...HasuraQueries.account.search._ctx.getAccountNumbersByCoreIds(coreIds),
      queryKey: ["accountNumberMap", JSON.stringify(coreIds)],
      useRawResponse: true,
      placeholderData: {},
      parserFn: (rawData) =>
        (rawData.billing_account ?? []).reduce<Record<number, string>>(
          (map, ba) => {
            map[Number(ba.id)] = ba.number
            return map
          },
          {},
        ),
    })
  }
}

export const AccountService = new AccountServiceClass()
